import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, AlertTriangle } from "lucide-react";

/* ------------------------------------------------------------------
 * Section éditoriale « Ça vous est déjà arrivé ? »
 * Composition masonry/constellation, 3 niveaux typographiques,
 * animation d'entrée douce au scroll (framer-motion + reduced-motion).
 * Aucune logique métier ici — la section se contente d'exposer les
 * situations et se termine par le CTA SOS Print existant.
 * ------------------------------------------------------------------ */

// Chaque carte porte son propre "poids" éditorial + son placement dans
// la grille 12 colonnes desktop (colStart / colSpan) puis les tailles
// tablette (mdSpan) et mobile (single column).
const SITUATION_CARDS = [
  {
    id: "canva",
    variant: "hero",
    text: "Vous l'avez créé sur Canva. Votre imprimeur, lui, aimerait surtout pouvoir l'imprimer.",
    colStart: 1, colSpan: 7, mdSpan: 2,
  },
  {
    id: "logo-capture",
    variant: "standard",
    text: "Votre client vous a envoyé son logo… en capture d'écran ?",
    colStart: 8, colSpan: 5, mdSpan: 1,
  },
  {
    id: "quote-refus",
    variant: "punchline-hero",
    text: "« Mon imprimeur refuse mon fichier. »",
    colStart: 1, colSpan: 5, mdSpan: 1,
  },
  {
    id: "whatsapp",
    variant: "standard",
    text: "Un JPEG minuscule, un logo pixelisé, une capture WhatsApp…",
    colStart: 6, colSpan: 7, mdSpan: 2,
  },
  {
    id: "jargon",
    variant: "hero",
    text: "Votre imprimeur parle PDF/X, fond perdu et 300 dpi… et vous avez décroché à la deuxième phrase ?",
    colStart: 1, colSpan: 8, mdSpan: 2,
  },
  {
    id: "quote-tel",
    variant: "punchline",
    text: "« Mais sur mon téléphone, le logo est très net ! »",
    colStart: 9, colSpan: 4, mdSpan: 1,
  },
  {
    id: "beau-ecran",
    variant: "punchline",
    text: "Beau à l'écran ne veut pas toujours dire imprimable.",
    colStart: 1, colSpan: 4, mdSpan: 1,
  },
  {
    id: "entente",
    variant: "hero",
    text: "Vous avez le visuel. Votre imprimeur a les exigences techniques. Nous faisons en sorte que les deux s'entendent.",
    colStart: 5, colSpan: 8, mdSpan: 2,
  },
  {
    id: "no-restart",
    variant: "standard",
    text: "Ne recommencez pas votre création. Faites-la simplement devenir imprimable.",
    colStart: 1, colSpan: 6, mdSpan: 1,
  },
  {
    id: "conclusion",
    variant: "hero-conclusion",
    text: "Votre fichier a été refusé ? Avant de tout refaire, envoyez-le-nous.",
    colStart: 7, colSpan: 6, mdSpan: 2,
  },
];

const VARIANT_STYLES = {
  hero: "text-2xl lg:text-3xl leading-snug font-serif text-[#FAF8F5]",
  standard: "text-lg lg:text-xl leading-snug font-serif text-neutral-200",
  punchline: "text-xl lg:text-2xl leading-tight font-serif italic text-[#FAF8F5]",
  "punchline-hero": "text-2xl lg:text-4xl leading-tight font-serif italic text-[#FAF8F5]",
  "hero-conclusion": "text-2xl lg:text-3xl leading-snug font-serif text-[#FAF8F5]",
};

// Fond/bordure très légèrement modulé : les hero ont un fond un peu
// plus clair et une bordure accent discrète pour créer la hiérarchie
// sans multiplier les couleurs.
const VARIANT_SURFACE = {
  hero:              "bg-[#1a1a1a] border-white/15",
  standard:          "bg-[#141414] border-white/10",
  punchline:         "bg-transparent border-white/10 border-dashed",
  "punchline-hero":  "bg-[#1a1a1a] border-[#0E7C90]/40",
  "hero-conclusion": "bg-[#1a1a1a] border-[#0E7C90]/60",
};

function SituationCard({ card, index, reduced }) {
  const initial = reduced
    ? { opacity: 0 }
    : { opacity: 0, y: 24, x: index % 2 === 0 ? -6 : 6 };
  const animate = reduced
    ? { opacity: 1 }
    : { opacity: 1, y: 0, x: 0 };

  return (
    <motion.article
      initial={initial}
      whileInView={animate}
      viewport={{ once: true, margin: "-10% 0px -10% 0px", amount: 0.2 }}
      transition={{
        duration: reduced ? 0.2 : 0.6,
        delay: reduced ? 0 : Math.min(index * 0.06, 0.35),
        ease: [0.22, 0.61, 0.36, 1],
      }}
      className={
        "situation-card group border p-6 lg:p-8 transition-[transform,box-shadow,border-color] duration-300 " +
        "hover:-translate-y-[2px] hover:shadow-[0_18px_40px_-24px_rgba(14,124,144,0.55)] " +
        VARIANT_SURFACE[card.variant]
      }
      data-testid={`situation-${card.id}`}
      data-col-start={card.colStart}
      data-col-span={card.colSpan}
      data-md-span={card.mdSpan}
    >
      <p className={VARIANT_STYLES[card.variant]}>{card.text}</p>
    </motion.article>
  );
}

function SituationsSection() {
  const reduced = useReducedMotion();

  return (
    <section className="border-b border-white/10" data-testid="sos-situations">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-24 lg:py-28">
        <motion.header
          initial={reduced ? { opacity: 0 } : { opacity: 0, y: 12 }}
          whileInView={reduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5, ease: [0.22, 0.61, 0.36, 1] }}
          className="max-w-3xl"
        >
          <div className="cs-eyebrow text-[#0E7C90]">Situations reconnues</div>
          <h2 className="cs-h2 mt-4 text-4xl lg:text-5xl text-[#FAF8F5]">
            Ça vous est déjà arrivé&nbsp;?
          </h2>
          <p className="mt-5 text-neutral-300 text-[15px] sm:text-base leading-relaxed">
            Entre Canva, captures d'écran, logos flous et messages incompréhensibles de l'imprimeur…
            beaucoup de projets coincent juste avant l'impression.
          </p>
        </motion.header>

        {/* Constellation de cartes — grille 12 col desktop, 2 col tablette, 1 col mobile */}
        <div className="mt-14 lg:mt-16 sos-situations-grid">
          {SITUATION_CARDS.map((c, i) => (
            <SituationCard key={c.id} card={c} index={i} reduced={reduced} />
          ))}
        </div>

        {/* Transition vers la solution */}
        <motion.div
          initial={reduced ? { opacity: 0 } : { opacity: 0, y: 16 }}
          whileInView={reduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, delay: reduced ? 0 : 0.1, ease: [0.22, 0.61, 0.36, 1] }}
          className="mt-16 lg:mt-20 border-t border-white/10 pt-14 lg:pt-16 max-w-3xl"
        >
          <h3 className="cs-h3 text-3xl lg:text-4xl text-[#FAF8F5] leading-snug">
            Vous vous reconnaissez dans l'une de ces situations&nbsp;?
          </h3>
          <p className="mt-5 text-neutral-300 text-[15px] sm:text-base leading-relaxed">
            Envoyez-nous votre fichier, et le message de votre imprimeur si vous l'avez.
            Nous nous occupons du reste.
          </p>
          <div className="mt-8">
            <Link
              to="/configurer/conforme?situation=refused"
              className="cs-btn-primary"
              data-testid="sos-situations-cta"
            >
              Corriger mon fichier <ArrowUpRight size={18} />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

const CHECKS = [
  { title: "Fond perdu manquant", text: "Votre PDF est refusé car il n'a pas de bords à couper." },
  { title: "Mauvais format", text: "Votre document ne correspond pas au gabarit imprimeur." },
  { title: "Résolution insuffisante", text: "Vos images sont trop faibles pour une impression HD." },
  { title: "PDF refusé", text: "Le fichier ne passe pas les contrôles automatiques de l'imprimeur." },
  { title: "Colorimétrie CMJN", text: "Votre document est en RVB, il faut le convertir proprement." },
  { title: "Polices manquantes", text: "Les typographies ne sont pas intégrées ou vectorisées." },
];

const CASES = [
  {
    key: "conforme",
    tag: "Prestation unique",
    title: "Rendre mon fichier conforme à l'imprimeur",
    price: 29,
    example: "Consignes techniques, fichier refusé, problème signalé ou message d'erreur que vous ne comprenez pas — envoyez votre fichier et les indications reçues, Charbonier Studio s'occupe des corrections.",
    cta: "Résoudre mon problème d'impression",
    to: "/configurer/conforme?situation=refused",
  },
];

export default function SosPrint() {
  return (
    <div data-testid="page-sos" className="cs-dark cs-grain">
      <section className="border-b border-white/10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="cs-eyebrow text-[#0E7C90]">SOS Print · Urgence prépresse</div>
          <h1 className="cs-h1 mt-6 text-5xl sm:text-6xl lg:text-8xl">
            Votre imprimeur<br />refuse votre fichier <span className="text-[#0E7C90]">?</span>
          </h1>
          <p className="mt-8 text-neutral-300 max-w-2xl text-lg leading-relaxed">
            Envoyez-nous votre fichier et les indications reçues de votre imprimeur. Charbonier Studio prend en charge
            l'analyse, la correction et le renvoi d'un fichier conforme, prêt à passer en machine.
          </p>
        </div>
      </section>

      {/* Une seule prestation à 29 € */}
      <section className="border-b border-white/10" data-testid="sos-cases">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-20">
          <div className="cs-eyebrow text-neutral-400">Une seule prestation</div>
          <h2 className="cs-h2 mt-4 text-4xl lg:text-5xl">Rendre votre fichier conforme à l'imprimeur.</h2>
          <p className="mt-4 max-w-2xl text-neutral-400 text-[15px] sm:text-base">
            Consignes techniques, fichier refusé ou problème signalé — un tarif unique. Vous n'avez pas à identifier vous-même le problème.
          </p>

          <div className="mt-12 grid md:grid-cols-1 gap-6">
            {CASES.map((c) => (
              <div key={c.key} className="border border-white/15 bg-[#1a1a1a] p-8 flex flex-col md:flex-row md:items-start gap-6" data-testid={`sos-case-${c.key}`}>
                <div className="md:w-1/3">
                  <div className="cs-eyebrow text-[#0E7C90]">{c.tag}</div>
                  <h3 className="cs-h3 mt-4 text-2xl lg:text-3xl text-[#FAF8F5]">{c.title}</h3>
                  <div className="mt-6">
                    <div className="text-[11px] font-mono tracking-[0.14em] uppercase text-neutral-500">Tarif unique</div>
                    <div className="font-serif text-5xl text-[#FAF8F5] mt-1">{c.price} €</div>
                  </div>
                </div>
                <div className="md:flex-1 md:border-l md:border-white/10 md:pl-6">
                  <div className="cs-eyebrow text-neutral-500">Ce qui est inclus</div>
                  <p className="mt-3 text-[15px] text-neutral-300 leading-relaxed">{c.example}</p>
                  <Link to={c.to} className="mt-6 cs-btn-primary" data-testid={`sos-case-cta-${c.key}`}>
                    {c.cta} <ArrowUpRight size={16} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-white/10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-20">
          <div className="cs-eyebrow text-neutral-400">Symptômes fréquents</div>
          <h2 className="cs-h2 mt-4 text-4xl lg:text-5xl">Ce qui bloque votre impression.</h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-white/15">
            {CHECKS.map((c, i) => (
              <div key={i} className="p-8 border-r border-b border-white/15">
                <AlertTriangle size={22} className="text-[#0E7C90]" />
                <h3 className="cs-h3 mt-4 text-xl">{c.title}</h3>
                <p className="mt-2 text-[15px] text-neutral-400 leading-relaxed">{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SituationsSection />

      <section>
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-20 text-center">
          <h3 className="cs-h2 text-4xl lg:text-5xl">Envoyez, on s'occupe du reste.</h3>
          <p className="mt-4 text-neutral-300 max-w-2xl mx-auto text-[15px] sm:text-base">
            Réponse sous 24 à 48 h ouvrées.
          </p>
          <div className="mt-8 flex justify-center flex-wrap gap-4">
            <Link to="/configurer/conforme?situation=refused" className="cs-btn-primary" data-testid="sos-cta-conforme">Résoudre mon problème d'impression — 29 € <ArrowUpRight size={18} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
